import React, { type FC, useState, useEffect } from 'react'
import { RequiredFieldHelperText, skeletonStyles, ChamberSelect, CommitteeTypeSelect, UsStateSelect, IconButtonWithTooltip } from 'components'
import { FormControl, Skeleton, Stack, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, Typography, CircularProgress, InputBase, Paper, Divider, Grid, FormControlLabel, Checkbox } from '@mui/material'
import { type CommitteeBasicInformation, getCaucusCodes, getCommitteeCodes } from 'services'
import { type CommitteeCreationInfo } from 'helpers'
import { US_COMMITTEE_ROLES, CHAMBERS } from 'utils'
import InfoIcon from '@mui/icons-material/Info'
import SearchIcon from '@mui/icons-material/Search'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

interface CommitteeCode {
  id: number
  code: string
  name: string
}

interface CommitteeBasicInfoFieldsProps {
  loading: boolean
  disabled: boolean
  committee: CommitteeBasicInformation | CommitteeCreationInfo
  updateValue: (
    updater: (prevState: any) => any
  ) => void
  isFieldEmpty: (key: string) => boolean
  errorFields: string[]
  showSubs?: boolean
}

export const CommitteeBasicInfoFields: FC<CommitteeBasicInfoFieldsProps> = ({ loading, disabled, committee, updateValue, errorFields, isFieldEmpty, showSubs = true }) => {
  const [openCodesDialog, setOpenCodesDialog] = useState(false)
  const [committeeCodes, setCommitteeCodes] = useState<CommitteeCode[]>([])
  const [loadingCodes, setLoadingCodes] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredCodes, setFilteredCodes] = useState<CommitteeCode[]>([])
  const [openCommitteeCodesDialog, setOpenCommitteeCodesDialog] = useState(false)
  const [committeeCodesList, setCommitteeCodesList] = useState<CommitteeCode[]>([])
  const [loadingCommitteeCodes, setLoadingCommitteeCodes] = useState(false)

  const isCaucus = (): boolean => {
    return committee.role?.id === US_COMMITTEE_ROLES.FED_CAUCUS || committee.role?.id === US_COMMITTEE_ROLES.STATE_CAUCUS
  }

  const setDefaultCaucusCode = (): void => {
    if (committee.chamber?.id === undefined || committee.chamber?.id === null) return

    const DEFAULT_CAUCUS_CODES: Record<number, string> = {
      [CHAMBERS.US_SENATE.id]: 'SCAUC999',
      [CHAMBERS.STATE_SENATE.id]: 'SCAUC999',
      [CHAMBERS.US_HOUSE.id]: 'HCAUC9999',
      [CHAMBERS.STATE_HOUSE.id]: 'HCAUC9999',
      [CHAMBERS.US_JOINT.id]: 'JCAUC999',
      [CHAMBERS.STATE_JOINT.id]: 'JCAUC999'
    }

    const defaultCodes = Object.values(DEFAULT_CAUCUS_CODES)
    const isCodeEmptyOrDefault = committee.code === '' ||
                                 committee.code === null ||
                                 defaultCodes.includes(committee.code)

    if (!isCodeEmptyOrDefault) return

    const defaultCode = DEFAULT_CAUCUS_CODES[committee.chamber.id] ?? ''

    if (defaultCode !== '') {
      updateField('code', defaultCode)
    }
  }

  useEffect(() => {
    if (isCaucus()) {
      setDefaultCaucusCode()
    }
  }, [committee.role?.id, committee.chamber?.id])

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCodes(committeeCodes)
    } else {
      const lowercaseSearch = searchTerm.toLowerCase()
      setFilteredCodes(
        committeeCodes.filter(
          code =>
            code.code.toLowerCase().includes(lowercaseSearch) ||
            code.name.toLowerCase().includes(lowercaseSearch)
        )
      )
    }
  }, [searchTerm, committeeCodes])

  const updateField = (field: string, newValue: any): void => {
    updateValue((prevState) => {
      return { ...prevState, [field]: newValue }
    })
  }

  const isError = (field: string): boolean => {
    return errorFields.includes(field)
  }

  const handleOpenCodesDialog = async (): Promise<void> => {
    setOpenCodesDialog(true)
    setSearchTerm('')
    try {
      setLoadingCodes(true)
      const chamberId = committee.chamber?.id

      const params: any = { chamber: chamberId }
      if (chamberId === CHAMBERS.STATE_SENATE.id || chamberId === CHAMBERS.STATE_HOUSE.id) {
        if (committee.state?.id !== undefined && committee.state?.id !== null) {
          params.state = committee.state.id
        }
      }

      const codes = await getCaucusCodes(params)
      setCommitteeCodes(codes)
      setFilteredCodes(codes)
    } catch (error) {
      console.error('Failed to load committee codes:', error)
    } finally {
      setLoadingCodes(false)
    }
  }

  const handleOpenCommitteeCodesDialog = async (): Promise<void> => {
    setOpenCommitteeCodesDialog(true)

    try {
      setLoadingCommitteeCodes(true)
      const chamberId = committee.chamber?.id

      const params: any = { chamber: chamberId }

      const isStateChamber = chamberId != null && [CHAMBERS.STATE_SENATE.id, CHAMBERS.STATE_HOUSE.id, CHAMBERS.STATE_JOINT.id].includes(chamberId)

      if (isStateChamber && committee.state?.id != null) {
        params.state = committee.state.id
      }

      const committeeCodes = await getCommitteeCodes(params)
      setCommitteeCodesList(committeeCodes)
    } catch (error) {
      console.error('Failed to load committee codes:', error)
    } finally {
      setLoadingCommitteeCodes(false)
    }
  }

  return (
    <Stack spacing={1}>
      {loading
        ? (
          <>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} variant="rectangular" sx={skeletonStyles} />
            ))}
          </>
          )
        : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={10}>
                <TextField
                  id="Name"
                  disabled={disabled}
                  label="Name"
                  variant="outlined"
                  value={committee.name}
                  error={isError('name')}
                  onChange={(e: any) => { updateField('name', e.target.value) }}
                  fullWidth />
              </Grid>
              <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={disabled}
                      checked={committee.active ?? true}
                      onChange={(e) => { updateField('active', e.target.checked) }}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
            <RequiredFieldHelperText isEmpty={isFieldEmpty('name')}>
              Name field is required
            </RequiredFieldHelperText>

            <TextField
              id="displayName"
              disabled={disabled}
              label="Display Name"
              variant="outlined"
              error={isError('displayName')}
              value={committee.displayName}
              onChange={(e: any) => { updateField('displayName', e.target.value) } }
              fullWidth />

            <FormControl variant="outlined" fullWidth={true} error={isError('chamber')}>
            <ChamberSelect value={committee.chamber}
              updateChamber={(c) => { updateField('chamber', c) }}
              disabled={disabled || committee.parent != null}
            />
            </FormControl>
            <RequiredFieldHelperText isEmpty={isFieldEmpty('chamber')}>
              Chamber field is required
            </RequiredFieldHelperText>

            <FormControl variant="outlined" fullWidth={true} error={isError('role')}>
            <CommitteeTypeSelect value={committee.role}
              updateCommitteeType={(r) => { updateField('role', r) }}
              disabled={disabled || committee.parent != null} showSubs={showSubs} branchId={committee.role?.branch?.id}/>
            </FormControl>
            <RequiredFieldHelperText isEmpty={isFieldEmpty('role')}>
              Type field is required
            </RequiredFieldHelperText>

            <FormControl variant="outlined" fullWidth={true} error={isError('state')}>
            <UsStateSelect value={committee.state} updateState={(state) => { updateField('state', state) }} disabled={disabled || committee.parent != null}/>
            </FormControl>
            <RequiredFieldHelperText isEmpty={isFieldEmpty('state')}>
              State field is required
            </RequiredFieldHelperText>

            {/* Only show caucus code field for caucuses */}
            {isCaucus() && (
              <>
                <TextField
                  id="caucusCode"
                  disabled={disabled}
                  label="Caucus Code"
                  variant="outlined"
                  value={committee.code}
                  error={isError('code')}
                  onChange={(e: any) => { updateField('code', e.target.value) } }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButtonWithTooltip
                          label="View All Committee Codes"
                          color="primary"
                          onClick={() => { void handleOpenCodesDialog() }}
                          icon={<InfoIcon />}
                          disabled={disabled}
                        />
                      </InputAdornment>
                    )
                  }}
                  fullWidth />
                <RequiredFieldHelperText isEmpty={isFieldEmpty('code')}>
                  Code field is required
                </RequiredFieldHelperText>
              </>
            )}

            <TextField
              id="committeeCode"
              disabled={disabled}
              label="Committee Code"
              variant="outlined"
              value={committee.code ?? ''}

              onChange={(e) => { updateField('code', e.target.value) }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButtonWithTooltip
                      label="View Committee Code List"
                      color="primary"
                      onClick={() => { void handleOpenCommitteeCodesDialog() }}
                      icon={<OpenInNewIcon />}
                      disabled={disabled}
                    />
                  </InputAdornment>
                )
              }}
              fullWidth />
            <RequiredFieldHelperText isEmpty={isFieldEmpty('code')}>
              Code field is required
            </RequiredFieldHelperText>

            {/* Committee Codes Dialog - only needed for caucuses */}
            {isCaucus() && (
              <Dialog
                open={openCodesDialog}
                onClose={() => { setOpenCodesDialog(false) }}
                aria-labelledby="committee-codes-dialog-title"
                maxWidth="md"
                fullWidth
              >
                <DialogTitle id="committee-codes-dialog-title">
                  Caucus Codes {committee.chamber?.desc !== undefined && committee.chamber?.desc !== null && committee.chamber?.desc !== ''
                  ? `for ${committee.chamber.desc}${committee.state?.desc !== undefined && committee.state?.desc !== null && committee.state?.desc !== 'All'
                        ? ` - ${committee.state.desc}`
                        : ''}`
                  : ''}
                </DialogTitle>
                <DialogContent dividers>
                  {loadingCodes
                    ? (
                      <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
                        <CircularProgress />
                        <Typography>Loading caucus codes...</Typography>
                      </Stack>
                      )
                    : (
                      <>
                        {/* Search bar */}
                        <Paper
                          component="form"
                          sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', mb: 2 }}
                        >
                          <InputBase
                            sx={{ ml: 1, flex: 1 }}
                            placeholder="Search codes or names"
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value)
                            }}
                            inputProps={{ 'aria-label': 'search committee codes' }}
                          />
                          <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                          <SearchIcon sx={{ p: '10px', color: 'action.active' }} />
                        </Paper>

                        <Typography variant="subtitle1" gutterBottom>
                          {filteredCodes.length} of {committeeCodes.length} codes found. Click on a code to use it.
                        </Typography>

                        <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
                          {filteredCodes.map((code) => (
                            <ListItem
                              key={code.id}
                              button
                              onClick={() => {
                                updateField('code', code.code)
                                setOpenCodesDialog(false)
                              }}
                            >
                              <ListItemText
                                primary={`${code.code} - ${code.name}`}
                              />
                            </ListItem>
                          ))}
                        </List>

                        {filteredCodes.length === 0 && (
                          <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 2 }}>
                            {committeeCodes.length === 0
                              ? 'No codes found for the current chamber.'
                              : 'No codes match your search.'}
                          </Typography>
                        )}
                      </>
                      )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => { setOpenCodesDialog(false) }}>
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            )}

            <Dialog
              open={openCommitteeCodesDialog}
              onClose={() => { setOpenCommitteeCodesDialog(false) }}
              aria-labelledby="committee-codes-dialog-title"
              aria-describedby="committee-codes-dialog-description"
              maxWidth="md"
              fullWidth
            >
              <DialogTitle id="committee-codes-dialog-title">
                Committee Code List {committee.chamber?.desc !== undefined && committee.chamber?.desc !== null && committee.chamber?.desc !== ''
                ? `for ${committee.chamber.desc}${committee.state?.desc !== undefined && committee.state?.desc !== null && committee.state?.desc !== 'All'
                      ? ` - ${committee.state.desc}`
                      : ''}`
                : ''}
              </DialogTitle>
              <DialogContent dividers>
                {loadingCommitteeCodes
                  ? (
                    <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
                      <CircularProgress />
                      <Typography>Loading committee codes...</Typography>
                    </Stack>
                    )
                  : (
                    <>
                      <Typography variant="subtitle1" gutterBottom id="committee-codes-dialog-description">
                        The committee codes for the selected chamber and selected state, if applicable, are shown below in actual display order.
                      </Typography>

                      <List sx={{ maxHeight: '400px', overflow: 'auto' }} aria-label="Committee codes">
                        {committeeCodesList.map((code) => (
                          <ListItem key={code.id}>
                            <ListItemText
                              primary={
                                <>
                                  <strong>{code.code}</strong> - {code.name}
                                </>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>

                      {committeeCodesList.length === 0 && (
                        <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 2 }}>
                          No committee codes found for the current chamber.
                        </Typography>
                      )}
                    </>
                    )}
              </DialogContent>
            </Dialog>
          </>
          )}
    </Stack>
  )
}
